import { z } from "zod";

// Mirrors the backend's ChangeManagementPlanSchema
// (backend/src/app/dtos/changeManagementPlan.dto.ts). The backend validates
// strictly on save, so this only needs to recognise the current shape;
// older snapshots fail to parse and are shown as raw JSON.
const reason = { why: z.string(), source_url: z.string().optional() };

export const changeManagementOperationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("replace_text"),
    old_text: z.string(),
    new_text: z.string(),
    ...reason,
  }),
  z.object({
    type: z.literal("add_text"),
    anchor_text: z.string(),
    placement: z.enum(["before", "after"]),
    new_text: z.string(),
    ...reason,
  }),
  z.object({
    type: z.literal("delete_text"),
    old_text: z.string(),
    ...reason,
  }),
  z.object({
    type: z.literal("replace_data_key_answer"),
    data_key: z.string(),
    old_value: z.unknown(),
    new_value: z.unknown(),
    ...reason,
  }),
  z.object({
    type: z.literal("add_data_key_answer"),
    data_key: z.string(),
    new_value: z.unknown(),
    ...reason,
  }),
  z.object({
    type: z.literal("delete_data_key_answer"),
    data_key: z.string(),
    old_value: z.unknown(),
    ...reason,
  }),
  z.object({
    type: z.literal("human_review"),
    question: z.string(),
    ...reason,
  }),
  z.object({
    type: z.literal("create_document"),
    template_to_create: z.string().nullable(),
    ...reason,
  }),
]);

export const changeManagementPlanSchema = z.object({
  status: z.enum(["ok", "partial"]),
  change_type: z.enum(["bugFix", "patch", "minor", "major"]).nullable(),
  change_documentation_sources: z.array(
    z.object({
      system: z.string(),
      refTitle: z.string(),
      url: z.string().optional(),
    })
  ),
  process_sources: z.array(
    z.object({
      role: z.enum(["governing", "supporting"]),
      title: z.string(),
      document_id: z.string().optional(),
      version_id: z.string().optional(),
      url: z.string().optional(),
    })
  ),
  summary: z.object({
    product: z.string(),
    plan: z.string(),
    impact: z.object({
      level: z.enum(["low", "medium", "high"]).nullable(),
      why: z.string(),
    }),
  }),
  recommendations: z.array(
    z.object({
      document: z.string(),
      document_id: z.string().optional(),
      version_id: z.string().optional(),
      changes_summary: z.string(),
      operations: z.array(changeManagementOperationSchema),
    })
  ),
  issues: z.array(
    z.object({ issue: z.string(), why: z.string(), next_step: z.string() })
  ),
});

export type ChangeManagementPlan = z.infer<typeof changeManagementPlanSchema>;
export type ChangeManagementRecommendation =
  ChangeManagementPlan["recommendations"][number];
export type ChangeManagementOperation = z.infer<
  typeof changeManagementOperationSchema
>;
export type ChangeManagementChangeType = ChangeManagementPlan["change_type"];
export type ChangeManagementImpactLevel =
  ChangeManagementPlan["summary"]["impact"]["level"];

export type ChangeManagementPlanRecord = {
  id: string;
  createdAt: string;
  plan: ChangeManagementPlan;
};

/** Returns null for plans saved in an older format, which are not shown. */
export const parseChangeManagementPlan = (raw: unknown) => {
  const result = changeManagementPlanSchema.safeParse(raw);
  return result.success ? result.data : null;
};
